/**
 * Build Your Own Routine (BYOR)
 *
 * Layout (from byor.liquid):
 *  - section.blocks = N selection steps. The review page is rendered
 *    as an ADDITIONAL group appended after the blocks loop, so the DOM
 *    has N + 1 routine-group elements and N + 1 nav items.
 *  - Each selection group has a grid of products. Each product is either
 *    a single-variant button (data-routine-select) or a multi-variant
 *    quickview trigger that opens byor_quickview in a modal.
 *  - The review group contains [data-routine-selected] (filled by JS),
 *    [data-auto-gift-variant-id] (server-rendered from the section's
 *    automatic gift list), and [data-routine-submit] (the add-to-cart
 *    trigger).
 *
 * Cart line-item properties added to every routine item:
 *   _gid          - random int shared by every item in this routine
 *   _routineType  - section.settings.routine_type
 *   _routineSize  - total number of items (selections + gifts)
 *   _routineGift  - 'TRUE' on gift lines only
 */

const BYOR_EVENTS = {
  productSelected: 'byor:product-selected',
};

class BuildYourRoutine extends HTMLElement {
  constructor() {
    super();

    this.routineType = this.dataset.routineType || '';
    // data-total-steps is section.blocks.size — the number of selection steps.
    // The review page is appended as an extra group at index = selectionCount.
    this.totalSteps = parseInt(this.dataset.totalSteps, 10) || 0;
    this.selectionCount = this.totalSteps;

    // Discount multiplier: e.g. data-discount-percent="30" → 0.7. Clamped
    // to [0, 1] so a bad value can't invert or amplify prices.
    const discountPercent = parseFloat(this.dataset.discountPercent);
    const off = Number.isFinite(discountPercent) ? discountPercent : 0;
    this.discountMultiplier = Math.max(0, Math.min(1, (100 - off) / 100));

    this.currentStep = 0;

    this.groups = Array.from(this.querySelectorAll('.routine-group'));
    this.navigationItems = Array.from(
      this.querySelectorAll('.routine-navigation__item')
    );
    this.reviewGroup = this.querySelector('.routine-group--review');
    this.selectedContainer = this.querySelector('[data-routine-selected]');
    this.submitButton = this.querySelector('[data-routine-submit]');

    // Each step asks for `data-quantity` products (1 unless the merchant said
    // otherwise). bundle[i] is the list of picks for step i, so a step is done
    // once its list reaches stepQuantities[i], and `requiredCount` — not the
    // number of steps — is what the tracker counts against.
    this.stepQuantities = [];
    for (let i = 0; i < this.selectionCount; i += 1) {
      const group = this.groups[i];
      const quantity = group ? parseInt(group.dataset.quantity, 10) : 1;
      this.stepQuantities.push(quantity > 0 ? quantity : 1);
    }
    this.requiredCount = this.stepQuantities.reduce((sum, n) => sum + n, 0);
    this.bundle = this.stepQuantities.map(() => []);

    // Gift steps: block indices whose routine-group has data-is-gift="true".
    // Selections made while `currentStep` is in this set are tagged as gifts
    // and priced at 0 in the tracker.
    this.giftSteps = new Set();
    this.groups.forEach((group, i) => {
      if (group.dataset.isGift === 'true') this.giftSteps.add(i);
    });

    // Automatic gifts: server-rendered, added to cart unconditionally.
    // Their variant ids live on [data-auto-gift-variant-id] elements in the
    // review section; JS just reads them and tags each with _routineGift on
    // cart submit. They don't count toward the tracker (not user-chosen).
    this.autoGiftElements = Array.from(
      this.querySelectorAll('[data-auto-gift-variant-id]')
    );

    // `gid` groups every line item that belongs to a single routine "session"
    // — bundle picks, gifts, AND any upsell products the user clicks on the
    // review page. Same gid → backend can reconcile them. We hold it here so
    // upsell add-to-cart submissions (which happen BEFORE the tracker CTA)
    // can share the same value; addToCart() rotates it after a successful
    // submit so a follow-up add produces a distinct bundle.
    this.gid = this.newGid();

    // Upsell products render via card-product; each <li data-routine-upsell>
    // contains a Shopify product form. We inject `_upsell` + `_gid` as hidden
    // fields so any add-to-cart from those cards carries the properties.
    this.upsellForms = Array.from(
      this.querySelectorAll('[data-routine-upsell] form')
    );
    this.upsellForms.forEach((form) => this.syncUpsellFields(form));

    // Sticky tracker bar (count + price + progress + CTA).
    this.tracker = {
      root: this.querySelector('[data-routine-tracker]'),
      count: this.querySelector('[data-routine-tracker-count]'),
      priceWrap: this.querySelector('[data-routine-tracker-price]'),
      priceSale: this.querySelector('[data-routine-tracker-price-sale]'),
      priceOriginal: this.querySelector(
        '[data-routine-tracker-price-original]'
      ),
      progress: this.querySelector('[data-routine-tracker-progress]'),
      progressFill: this.querySelector('[data-routine-tracker-progress-fill]'),
      cta: this.querySelector('[data-routine-tracker-cta]'),
    };
    // Read the templates from window.routine_language — the rendered strings
    // are no good because Liquid already substituted their placeholders
    // server-side, leaving nothing for JS to replace.
    const lang = window.routine_language || {};
    this.ctaTemplates = {
      pickMore: lang.pick_more || 'pick {{ count }} more',
      addToCart: lang.add_to_cart || 'add to cart',
      stepProgress: lang.step_progress || '{{ count }} of {{ total }} chosen',
    };

    // Currency formatter for the tracker totals.
    this.currency =
      (window.Shopify &&
        window.Shopify.currency &&
        window.Shopify.currency.active) ||
      'USD';
    this.locale = document.documentElement.lang || undefined;

    this.bindEvents();
    this.stepQuantities.forEach((_, i) => this.updateStepState(i));
    this.updateNavState();
    this.updateTracker();
  }

  bindEvents() {
    // Single-variant "add to routine" buttons inside selection steps.
    this.querySelectorAll('[data-routine-select]').forEach((btn) => {
      btn.addEventListener('click', this.onSingleVariantClick.bind(this));
    });

    // Per-tile "remove" buttons, shown once a tile has at least one pick.
    this.querySelectorAll('[data-routine-remove]').forEach((btn) => {
      btn.addEventListener('click', this.onRemoveClick.bind(this));
    });

    // Quantity steppers: the minus/plus buttons and the number field itself.
    this.querySelectorAll('[data-routine-step]').forEach((btn) => {
      btn.addEventListener('click', this.onStepperClick.bind(this));
    });

    this.querySelectorAll('[data-routine-quantity-input]').forEach((input) => {
      // `change` rather than `input` so typing "3" into a step that only wants
      // 3 doesn't advance the customer mid-keystroke.
      input.addEventListener('change', this.onStepperInput.bind(this));
    });

    // Navigation items (let user jump back to a completed step).
    this.navigationItems.forEach((item) => {
      item.addEventListener('click', this.onNavClick.bind(this));
    });

    // Back buttons (one per middle step).
    this.querySelectorAll('.routine-header__back').forEach((btn) => {
      btn.addEventListener('click', this.goBack.bind(this));
    });

    // Reset buttons (live on the review step).
    this.querySelectorAll('.routine-header__reset').forEach((btn) => {
      btn.addEventListener('click', this.reset.bind(this));
    });

    // Add-to-cart on review step.
    if (this.submitButton) {
      this.submitButton.addEventListener('click', this.addToCart.bind(this));
    }

    // Variant selection from the quickview modal.
    subscribe(BYOR_EVENTS.productSelected, this.onProductSelected.bind(this));
  }

  /* ------------------------------------------------------------------ */
  /* Selection handlers                                                  */
  /* ------------------------------------------------------------------ */

  /*
   * Every control reads its product from the tile it sits in, so the payload
   * is declared once in byor-product.liquid rather than per button.
   */
  tilePayload(tile) {
    if (!tile || !tile.dataset.variantId) return null;

    const data = tile.dataset;
    return {
      variantId: data.variantId,
      variantTitle: data.variantTitle || '',
      price: parseInt(data.variantPrice, 10) || 0,
      productId: data.productId,
      handle: data.productHandle,
      title: data.productTitle,
      image: data.productImage,
    };
  }

  /* Resolve the step a control belongs to from its enclosing group. */
  stepForElement(el) {
    const group = el.closest('.routine-group');
    if (!group) return -1;
    const step = parseInt(group.dataset.index, 10);
    return Number.isNaN(step) ? -1 : step;
  }

  onSingleVariantClick(e) {
    const btn = e.currentTarget;
    if (btn.disabled) return;

    const payload = this.tilePayload(btn.closest('[data-routine-tile]'));
    if (payload) this.selectProduct(payload);
  }

  onStepperClick(e) {
    const btn = e.currentTarget;
    if (btn.disabled) return;

    const tile = btn.closest('[data-routine-tile]');
    const step = this.stepForElement(btn);
    if (!tile || step < 0) return;

    const delta = parseInt(btn.dataset.routineStep, 10) || 0;
    this.setPickCount(step, tile, this.tileCount(step, tile) + delta);
  }

  onStepperInput(e) {
    const input = e.currentTarget;
    const tile = input.closest('[data-routine-tile]');
    const step = this.stepForElement(input);
    if (!tile || step < 0) return;

    const next = parseInt(input.value, 10);
    this.setPickCount(step, tile, Number.isNaN(next) ? 0 : next);
  }

  onProductSelected({ data }) {
    if (!data) return;
    this.selectProduct(data);
  }

  onRemoveClick(e) {
    const tile = e.currentTarget.closest('[data-routine-tile]');
    const step = this.stepForElement(e.currentTarget);
    if (!tile || step < 0) return;

    this.removePick(step, tile.dataset.variantId, tile.dataset.productId);
  }

  /*
   * A step marked `is_gift` overrides whatever the control sent: the entry is
   * always tagged as a gift. We keep the real variant price on it so the
   * tracker's "original" strikethrough total can include it (the customer
   * sees the retail value of what they'd normally pay). The "discounted"
   * total treats gifts as $0 — see updateTracker.
   */
  makeEntry(step, product) {
    return this.giftSteps.has(step)
      ? { ...product, isGift: true }
      : { ...product };
  }

  /* How many picks in this step belong to this tile. */
  tileCount(step, tile) {
    const { variantId, productId } = tile.dataset;
    return this.stepPicks(step).filter((pick) =>
      variantId
        ? String(pick.variantId) === String(variantId)
        : String(pick.productId) === String(productId)
    ).length;
  }

  /*
   * Stepper entry point: move a tile to an exact count, clamped to the room
   * the step has left. A single-pick step replaces its whole contents so
   * raising a second tile to 1 swaps the choice rather than being refused.
   */
  setPickCount(step, tile, requested) {
    if (step < 0 || step >= this.selectionCount) return;
    if (tile.dataset.soldOut) return;

    const payload = this.tilePayload(tile);
    if (!payload) return;

    const quantity = this.stepQuantities[step];
    const picks = this.stepPicks(step);
    const current = this.tileCount(step, tile);

    let next = Math.max(0, requested);

    if (quantity === 1) {
      next = Math.min(next, 1);
      if (next === 1) {
        this.bundle[step] = [this.makeEntry(step, payload)];
        this.afterSelectionChange(step, true);
        return;
      }
    } else {
      // Room left for this tile is the step's quantity minus everything
      // picked by the *other* tiles.
      next = Math.min(next, quantity - (picks.length - current));
    }

    if (next === current) {
      // Nothing changed, but a rejected keystroke may have left the input
      // showing a value we didn't accept — re-sync it.
      this.updateStepState(step);
      return;
    }

    if (next < current) {
      for (let i = current; i > next; i -= 1) {
        this.removeOnePick(
          step,
          tile.dataset.variantId,
          tile.dataset.productId
        );
      }
    } else {
      for (let i = current; i < next; i += 1) {
        this.bundle[step].push(this.makeEntry(step, payload));
      }
    }

    this.afterSelectionChange(step, next > current);
  }

  selectProduct(product) {
    if (!product || !product.variantId) return;

    const step = this.currentStep;
    if (step >= this.selectionCount) return;

    const entry = this.makeEntry(step, product);
    const quantity = this.stepQuantities[step];

    if (quantity === 1) {
      // Single-pick steps behave like a radio group: a second click swaps
      // the choice rather than being rejected as "already full".
      this.bundle[step] = [entry];
    } else if (this.stepPicks(step).length < quantity) {
      this.bundle[step].push(entry);
    } else {
      return;
    }

    this.afterSelectionChange(step, true);
  }

  /*
   * Drop the most recent pick matching a tile, without re-rendering. A
   * quickview tile has no variant of its own, so it falls back to matching on
   * product. Returns true when something was removed.
   */
  removeOnePick(step, variantId, productId) {
    const picks = this.stepPicks(step);

    for (let i = picks.length - 1; i >= 0; i -= 1) {
      const matches = variantId
        ? String(picks[i].variantId) === String(variantId)
        : productId != null && String(picks[i].productId) === String(productId);

      if (matches) {
        picks.splice(i, 1);
        return true;
      }
    }

    return false;
  }

  removePick(step, variantId, productId) {
    if (!this.removeOnePick(step, variantId, productId)) return;
    this.afterSelectionChange(step, false);
  }

  /*
   * Single place to re-sync everything a changed pick affects. `advance` is
   * true for additions only: filling the last slot of a step moves the
   * customer on, while removing a pick leaves them where they are.
   */
  afterSelectionChange(step, advance) {
    this.updateStepState(step);
    this.updateNavState();
    this.updateTracker();

    if (advance && this.isStepFull(step)) {
      this.goToNextEmpty();
    }
  }

  /* ------------------------------------------------------------------ */
  /* Step state                                                          */
  /* ------------------------------------------------------------------ */

  stepPicks(step) {
    return this.bundle[step] || [];
  }

  isStepFull(step) {
    return this.stepPicks(step).length >= this.stepQuantities[step];
  }

  allPicks() {
    return this.bundle.reduce((all, picks) => all.concat(picks), []);
  }

  /*
   * Reflect a step's picks back onto its tiles: which are chosen, how many
   * times, and how much room is left. Multi-pick steps lock their controls
   * once full so the only way forward is to take something back; single-pick
   * steps stay live so the customer can swap in one click.
   */
  updateStepState(step) {
    const group = this.groups[step];
    if (!group) return;

    const picks = this.stepPicks(step);
    const quantity = this.stepQuantities[step];
    const full = picks.length >= quantity;

    const byVariant = new Map();
    const byProduct = new Map();
    picks.forEach((pick) => {
      const variantKey = String(pick.variantId);
      const productKey = String(pick.productId);
      byVariant.set(variantKey, (byVariant.get(variantKey) || 0) + 1);
      byProduct.set(productKey, (byProduct.get(productKey) || 0) + 1);
    });

    group.querySelectorAll('[data-routine-tile]').forEach((tile) => {
      const { variantId, productId, soldOut } = tile.dataset;
      const count = variantId
        ? byVariant.get(String(variantId)) || 0
        : byProduct.get(String(productId)) || 0;

      tile.classList.toggle('routine-product--picked', count > 0);

      const badge = tile.querySelector('[data-routine-pick-count]');
      if (badge) {
        badge.textContent = String(count);
        badge.hidden = count === 0;
      }

      const remove = tile.querySelector('[data-routine-remove]');
      if (remove) remove.hidden = count === 0;

      const add = tile.querySelector('[data-routine-add]');
      if (add) {
        add.setAttribute('aria-pressed', count > 0 ? 'true' : 'false');
        if (!soldOut) add.disabled = quantity > 1 && full;
      }

      const input = tile.querySelector('[data-routine-quantity-input]');
      if (input && !soldOut) {
        input.value = String(count);

        const minus = tile.querySelector('[data-routine-step="-1"]');
        const plus = tile.querySelector('[data-routine-step="1"]');

        if (minus) minus.disabled = count === 0;
        // A single-pick step never locks its plus buttons: pressing one just
        // moves the choice over from whichever tile currently holds it.
        if (plus) plus.disabled = quantity > 1 && full;
      }
    });

    const progress = group.querySelector('[data-routine-step-progress]');
    if (progress) {
      progress.textContent = this.ctaTemplates.stepProgress
        .replace('{{ count }}', String(picks.length))
        .replace('{{ total }}', String(quantity));
    }
  }

  /* ------------------------------------------------------------------ */
  /* Navigation                                                          */
  /* ------------------------------------------------------------------ */

  onNavClick(e) {
    const item = e.currentTarget;
    const target = parseInt(item.dataset.index, 10);
    if (Number.isNaN(target)) return;

    // Only allow jumping to steps the user has already reached.
    // The current step is always reachable, and any prior step is reachable.
    // The review step is reachable only when every selection is filled.
    if (target > this.currentStep) {
      if (target === this.selectionCount && this.isComplete()) {
        // ok — jump forward to review
      } else {
        return;
      }
    }

    this.goToStep(target);
  }

  goBack() {
    this.goToStep(Math.max(0, this.currentStep - 1));
  }

  goToNextEmpty() {
    // After a selection, jump to the first step that still owes picks.
    // If every step is full, jump to the review step.
    const next = this.bundle.findIndex(
      (picks, i) => picks.length < this.stepQuantities[i]
    );
    if (next === -1) {
      this.goToStep(this.selectionCount); // review
    } else {
      this.goToStep(next);
    }
  }

  goToStep(index) {
    const clamped = Math.max(0, Math.min(index, this.groups.length - 1));
    this.currentStep = clamped;
    this.dataset.currentStep = clamped;

    this.groups.forEach((group, i) => {
      group.classList.toggle('active', i === clamped);
    });

    this.updateNavState();

    if (this.reviewGroup && this.groups[clamped] === this.reviewGroup) {
      this.renderReview();
    }

    this.scrollToTop();
    this.focusStepHeading(clamped);
  }

  focusStepHeading(index) {
    const group = this.groups[index];
    if (!group) return;
    const heading = group.querySelector('.routine-header__heading');
    if (!heading) return;
    if (!heading.hasAttribute('tabindex')) {
      heading.setAttribute('tabindex', '-1');
    }
    // preventScroll: our own scrollToTop is already doing the scroll work.
    try {
      heading.focus({ preventScroll: true });
    } catch (e) {
      heading.focus();
    }
  }

  updateNavState() {
    this.navigationItems.forEach((item, i) => {
      const isActive = i === this.currentStep;
      item.classList.toggle('active', isActive);
      if (isActive) {
        item.setAttribute('aria-current', 'step');
      } else {
        item.removeAttribute('aria-current');
      }
      // Mark items as "completed" so CSS can style visited ones differently.
      const filled = i < this.selectionCount && this.isStepFull(i);
      item.classList.toggle('completed', filled);
    });
  }

  /* ------------------------------------------------------------------ */
  /* Review + submit                                                     */
  /* ------------------------------------------------------------------ */

  isComplete() {
    return (
      this.bundle.length === this.selectionCount &&
      this.bundle.every((picks, i) => picks.length >= this.stepQuantities[i])
    );
  }

  renderReview() {
    if (!this.selectedContainer) return;

    this.selectedContainer.innerHTML = '';

    this.allPicks().forEach((product) => {
      if (!product) return;
      const item = document.createElement('div');
      const giftClass = product.isGift ? ' routine-product--gift' : '';
      item.className = `routine-product routine-product--selected${giftClass}`;
      const subtitle = product.isGift
        ? this.escape(
            (window.routine_language && window.routine_language.free_gift) ||
              'free gift'
          )
        : product.variantTitle && product.variantTitle !== 'Default Title'
        ? this.escape(product.variantTitle)
        : '';
      item.innerHTML = `
        <div class="routine-product__image">
          <div class="media media--portrait">
            ${
              product.image
                ? `<img src="${product.image}" alt="${this.escape(
                    product.title
                  )}" loading="lazy">`
                : ''
            }
          </div>
        </div>
        <div class="routine-product__details">
          <div class="routine-product__title body body--small">${this.escape(
            product.title || ''
          )}</div>
          ${
            subtitle
              ? `<div class="routine-product__description body body--xsmall">${subtitle}</div>`
              : ''
          }
        </div>
      `;
      this.selectedContainer.appendChild(item);
    });

    if (this.submitButton) {
      this.submitButton.disabled = !this.isComplete();
    }
  }

  /* ------------------------------------------------------------------ */
  /* Sticky tracker bar                                                  */
  /* ------------------------------------------------------------------ */

  updateTracker() {
    if (!this.tracker || !this.tracker.root) return;

    const picks = this.allPicks();
    const filledCount = picks.length;
    const complete = this.isComplete();

    // "X of N picked" counter, counting products rather than steps.
    if (this.tracker.count) {
      this.tracker.count.textContent = String(filledCount);
    }

    // Progress bar fill — % of the required picks made.
    const percent =
      this.requiredCount > 0 ? (filledCount / this.requiredCount) * 100 : 0;
    if (this.tracker.progressFill) {
      this.tracker.progressFill.style.width = `${percent}%`;
    }
    if (this.tracker.progress) {
      this.tracker.progress.setAttribute('aria-valuenow', String(filledCount));
    }

    // Subtotal.
    //
    // `originalCents` (strikethrough) is the total retail value the customer
    // would otherwise pay — includes every selection AND every gift, both
    // block-level "is_gift" picks and automatic gifts.
    //
    // `discountedCents` (sale) is what the customer actually pays: only the
    // non-gift selections, then × discountMultiplier. Gifts contribute $0
    // (backend function zeroes them out in cart via _routineGift).
    const bundleOriginalCents = picks.reduce((sum, item) => {
      if (!item || !item.price) return sum;
      return sum + item.price;
    }, 0);

    const autoGiftCents = this.autoGiftElements.reduce((sum, el) => {
      const p = parseInt(el.dataset.variantPrice, 10);
      return sum + (Number.isFinite(p) ? p : 0);
    }, 0);

    const originalCents = bundleOriginalCents + autoGiftCents;

    const paidBundleCents = picks.reduce((sum, item) => {
      if (!item || !item.price || item.isGift) return sum;
      return sum + item.price;
    }, 0);

    const discountedCents = Math.round(
      paidBundleCents * this.discountMultiplier
    );

    if (this.tracker.priceWrap) {
      if (filledCount === 0) {
        this.tracker.priceWrap.hidden = true;
      } else {
        this.tracker.priceWrap.hidden = false;
        if (this.tracker.priceSale) {
          this.tracker.priceSale.textContent =
            this.formatPrice(discountedCents);
        }
        if (this.tracker.priceOriginal) {
          this.tracker.priceOriginal.textContent =
            this.formatPrice(originalCents);
          this.tracker.priceOriginal.hidden = originalCents === discountedCents;
        }
      }
    }

    // CTA: disabled + "pick X more" until full, then "add to cart".
    if (this.tracker.cta) {
      this.tracker.cta.disabled = !complete;
      if (complete) {
        this.tracker.cta.textContent = this.ctaTemplates.addToCart;
      } else {
        const remaining = this.requiredCount - filledCount;
        this.tracker.cta.textContent = this.ctaTemplates.pickMore.replace(
          '{{ count }}',
          String(remaining)
        );
      }
    }
  }

  formatPrice(cents) {
    if (!Number.isFinite(cents)) return '';
    const dollars = cents / 100;
    const hasCents = cents % 100 !== 0;
    try {
      return new Intl.NumberFormat(this.locale, {
        style: 'currency',
        currency: this.currency,
        minimumFractionDigits: hasCents ? 2 : 0,
        maximumFractionDigits: 2,
      }).format(dollars);
    } catch (e) {
      return `$${dollars.toFixed(hasCents ? 2 : 0)}`;
    }
  }

  addToCart() {
    if (!this.submitButton || this.submitButton.disabled) return;
    if (!this.isComplete()) return;

    this.submitButton.disabled = true;
    this.submitButton.classList.add('loading');

    const gid = this.gid;

    const autoGiftVariantIds = this.autoGiftElements
      .map((el) => parseInt(el.dataset.autoGiftVariantId, 10))
      .filter((id) => !Number.isNaN(id));

    const picks = this.allPicks();
    const totalSize = picks.length + autoGiftVariantIds.length;

    // A multi-pick step lets the same variant be chosen more than once, so
    // collapse repeats into one line with a quantity instead of posting the
    // same id several times. Lines are keyed by id AND properties so a gift
    // pick never merges with a paid one.
    const lines = new Map();
    const addLine = (variantId, properties) => {
      const id = parseInt(variantId, 10);
      if (Number.isNaN(id)) return;

      const key = `${id}|${JSON.stringify(properties)}`;
      const existing = lines.get(key);

      if (existing) {
        existing.quantity += 1;
      } else {
        lines.set(key, { id, quantity: 1, properties });
      }
    };

    picks.forEach((product) => {
      if (!product || !product.variantId) return;
      const properties = {
        _gid: gid,
        _routineType: this.routineType,
        _routineSize: totalSize,
      };
      if (product.isGift) {
        properties._routineGift = 'TRUE';
      }
      addLine(product.variantId, properties);
    });

    autoGiftVariantIds.forEach((variantId) => {
      addLine(variantId, {
        _gid: gid,
        _routineType: this.routineType,
        _routineSize: totalSize,
        _routineGift: 'TRUE',
      });
    });

    const items = Array.from(lines.values());

    const cartAddUrl =
      (window.routes && window.routes.cart_add_url) || '/cart/add.js';
    const cartUrl = (window.routes && window.routes.cart_url) || '/cart';

    // Ask /cart/add.js to return the drawer sections in the same round trip,
    // the way product-form.js does, so the drawer can be re-rendered without
    // a second fetch. Falls back to a redirect when the theme is running in
    // "cart page" mode and no <cart-drawer> is present.
    const cartDrawer = document.querySelector('cart-drawer');
    const body = { items };
    if (cartDrawer && typeof cartDrawer.getSectionsToRender === 'function') {
      body.sections = cartDrawer
        .getSectionsToRender()
        .map((section) => section.id);
      body.sections_url = window.location.pathname;
    }

    fetch(cartAddUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(body),
    })
      .then((response) => response.json())
      .then((response) => {
        // /cart/add.js only sets `status` on an error payload (422 etc.).
        if (response.status) throw response;

        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'byor',
          cartData: response,
        });

        // Rotate the gid so the next add-to-cart (bundle re-submit OR another
        // upsell click) produces a distinct group in the cart.
        this.gid = this.newGid();
        this.upsellForms.forEach((form) => this.syncUpsellFields(form));

        if (cartDrawer && typeof cartDrawer.renderContents === 'function') {
          cartDrawer.renderContents(response);
          return;
        }

        window.location.href = cartUrl;
      })
      .catch((err) => {
        console.error('[byor] add to cart failed', err);
      })
      .finally(() => {
        // Re-enable so the user can add the same routine again — addToCart()
        // generates a fresh `_gid` on each call, so the next click produces a
        // new grouped bundle in the cart.
        this.submitButton.disabled = false;
        this.submitButton.classList.remove('loading');
      });
  }

  /* ------------------------------------------------------------------ */
  /* Reset                                                               */
  /* ------------------------------------------------------------------ */

  reset() {
    this.bundle = this.stepQuantities.map(() => []);
    this.bundle.forEach((_, i) => this.updateStepState(i));
    if (this.selectedContainer) this.selectedContainer.innerHTML = '';
    this.updateTracker();
    this.goToStep(0);
  }

  /* ------------------------------------------------------------------ */
  /* Helpers                                                             */
  /* ------------------------------------------------------------------ */

  scrollToTop() {
    const reduceMotion =
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const rect = this.getBoundingClientRect();
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const targetPosition = rect.top + scrollTop - 60;
    window.scrollTo({
      top: targetPosition,
      behavior: reduceMotion ? 'auto' : 'smooth',
    });
  }

  escape(str) {
    const div = document.createElement('div');
    div.textContent = String(str);
    return div.innerHTML;
  }

  newGid() {
    return Math.floor(Math.random() * 1_000_000_000);
  }

  /*
   * Inject/refresh `_upsell` and `_gid` hidden inputs on an upsell card's
   * product form. Called once at construct time for each form, then again
   * whenever this.gid rotates so subsequent adds carry the new group id.
   */
  syncUpsellFields(form) {
    if (!form) return;
    const ensure = (name, value) => {
      let input = form.querySelector(`input[type="hidden"][name="${name}"]`);
      if (!input) {
        input = document.createElement('input');
        input.type = 'hidden';
        input.name = name;
        form.appendChild(input);
      }
      input.value = value;
    };
    ensure('properties[_upsell]', 'true');
    ensure('properties[_gid]', String(this.gid));
  }
}

if (!customElements.get('build-your-routine')) {
  customElements.define('build-your-routine', BuildYourRoutine);
}

/* ==================================================================== */
/* Quickview trigger: opens the byor_quickview modal for a product       */
/* ==================================================================== */

class RoutineQuickviewTrigger extends HTMLElement {
  constructor() {
    super();

    this.productUrl = (this.dataset.productUrl || '').split('?')[0];
    this.button = this.querySelector('button');

    if (this.button) {
      this.button.addEventListener('click', this.onButtonClick.bind(this));
    }
  }

  onButtonClick(e) {
    e.preventDefault();

    fetch(`${this.productUrl}?section_id=byor_quickview`)
      .then((response) => response.text())
      .then((responseText) => {
        const responseHTML = new DOMParser().parseFromString(
          responseText,
          'text/html'
        );

        const oldModal = document.querySelector('#ByorQuickviewModal');
        if (oldModal) oldModal.remove();

        const modal = responseHTML.querySelector('#ByorQuickviewModal');
        if (!modal) return;

        document.body.appendChild(modal);
        modal.show(this.button);
      });
  }
}

if (!customElements.get('routine-quickview-trigger')) {
  customElements.define('routine-quickview-trigger', RoutineQuickviewTrigger);
}

/* ==================================================================== */
/* Quickview modal contents                                              */
/* ==================================================================== */

class RoutineQuickview extends HTMLElement {
  constructor() {
    super();

    this.productUrl = this.dataset.productUrl;

    // The submit button lives inside .quickview-container__info, which we
    // swap out on every variant change — use delegation so the listener
    // survives re-renders.
    this.addEventListener('click', this.onClick.bind(this));
  }

  // Re-fetch the modal contents when the user changes a variant option.
  // Subscribing here rather than in the constructor keeps the subscription
  // tied to the element's lifetime: each quickview open replaces the modal
  // in <body>, and a stale subscription on a detached modal would fire an
  // extra fetch for every modal ever opened.
  connectedCallback() {
    this.unsubscribeOptionChange = subscribe(
      PUB_SUB_EVENTS.optionValueSelectionChange,
      this.handleOptionValueChange.bind(this)
    );
  }

  disconnectedCallback() {
    if (!this.unsubscribeOptionChange) return;
    this.unsubscribeOptionChange();
    this.unsubscribeOptionChange = null;
  }

  onClick(e) {
    const submit = e.target.closest('[data-routine-quickview-submit]');
    if (!submit) return;
    if (submit.disabled) return;

    e.preventDefault();

    const product = this.getSelectedProduct(submit);
    if (!product) return;

    publish(BYOR_EVENTS.productSelected, { data: product });

    const modal = this.closest('modal-dialog');
    if (modal) modal.hide();
  }

  getSelectedProduct(submitButton) {
    // Prefer the live JSON the variant-selects component keeps in sync.
    const variantScript = this.querySelector(
      'variant-selects [data-selected-variant]'
    );
    let variant = null;
    if (variantScript) {
      try {
        variant = JSON.parse(variantScript.textContent);
      } catch (e) {
        variant = null;
      }
    }

    const variantId =
      (variant && variant.id) || submitButton.dataset.variantId || null;

    if (!variantId) return null;

    // Image: prefer the variant's featured image, fall back to whatever's
    // visible in the modal.
    let image =
      (variant && variant.featured_image && variant.featured_image.src) || null;
    if (!image) {
      const img = this.querySelector('.quickview-container__image img');
      if (img) image = img.currentSrc || img.src;
    }

    const titleEl = this.querySelector('.product__title h1, .product__title');
    const title =
      submitButton.dataset.productTitle ||
      (titleEl && titleEl.textContent.trim()) ||
      '';

    const variantTitle = (variant && variant.title) || '';
    const price = variant && Number.isFinite(variant.price) ? variant.price : 0;

    return {
      variantId: String(variantId),
      variantTitle,
      price,
      productId: submitButton.dataset.productId || '',
      handle: submitButton.dataset.productHandle || '',
      title,
      image,
    };
  }

  handleOptionValueChange({ data: { selectedOptionValues } }) {
    const productUrl = this.buildRequestUrlWithParams(selectedOptionValues);

    fetch(`${productUrl}`)
      .then((response) => response.text())
      .then((responseText) => {
        const responseHTML = new DOMParser().parseFromString(
          responseText,
          'text/html'
        );

        const quickviewContainer = responseHTML.querySelector(
          '#ByorQuickbuyContainer'
        );
        if (!quickviewContainer) return;

        const imageContainer = quickviewContainer.querySelector(
          '.quickview-container__image'
        );
        const infoContainer = quickviewContainer.querySelector(
          '.quickview-container__info'
        );

        if (imageContainer) {
          this.querySelector('.quickview-container__image').innerHTML =
            imageContainer.innerHTML;
        }
        if (infoContainer) {
          this.querySelector('.quickview-container__info').innerHTML =
            infoContainer.innerHTML;
        }
      });
  }

  buildRequestUrlWithParams(optionValues) {
    const params = [`section_id=byor_quickview`];
    if (optionValues && optionValues.length) {
      params.push(`option_values=${optionValues.join(',')}`);
    }
    return `${this.productUrl}?${params.join('&')}`;
  }
}

if (!customElements.get('routine-quickview')) {
  customElements.define('routine-quickview', RoutineQuickview);
}
